import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, authApi, teacherApi, studioApi } from '@/lib/api'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { PiiField } from '@/components/ui/PiiField'
import { Tabs } from '@/components/ui/Tabs'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { usePresentation } from '@/components/layout/AppShell'

// --- Types ---

interface UserProfile {
  user_id: string
  email: string
  name: string | null
  tier: string
  daily_streak: number
  longest_streak: number
  total_days_active: number
  total_credits: number
  last_activity_date: string | null
  created_at: number | null
  stripe_status: string | null
  stripe_mrr: number
  stripe_price_id: string | null
  stripe_cancel_at: number | null
  stripe_current_period_end: number | null
  trial_ends_at: number | null
  temp_access_granted_by: string | null
  temp_access_granted_at: number | null
  exerciseStats: { tool: string; attempts: number; avg_score: number; passed: number }[]
  emaProfile: Record<string, unknown> | null
}

interface HeatmapDay {
  date: string
  total_min: number
  ear_min: number
  sight_min: number
  knowledge_min: number
  uncategorized_min: number
}

interface HeatmapResponse {
  year: number
  days: HeatmapDay[]
  stats: {
    current_streak: number
    longest_streak: number
    total_days: number
    this_month_days: number
    this_month_total: number
    first_activity_date: string | null
  }
}

interface ExerciseResult {
  id: string
  exercise_id: string
  tool: string
  comparison_score: number | null
  passed: number
  duration_ms: number
  date: string
  created_at: string
}

interface ResultsResponse {
  results: ExerciseResult[]
  total: number
  page: number
  totalPages: number
}

interface ResultDetail extends ExerciseResult {
  user_id: string
  result_data: Record<string, unknown> | null
}

interface Transaction {
  id: number
  user_id: string
  amount: number
  reason: string
  balance_after: number
  created_at: string
}

interface TransactionsResponse {
  transactions: Transaction[]
  total: number
  page: number
  totalPages: number
}

// --- Helpers ---

const toolLabels: Record<string, string> = {
  intervals: 'Intervals',
  foundational: 'Foundational',
  'sight-reading': 'Sight Reading',
  vfe: 'VFE',
  vrp: 'VRP',
}

function formatDate(val: string | number | null): string {
  if (!val) return '--'
  const d = typeof val === 'number' ? new Date(val * 1000) : new Date(val)
  return isNaN(d.getTime()) ? '--' : d.toLocaleDateString()
}

function formatDuration(ms: number): string {
  if (!ms) return '--'
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rem = s % 60
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`
}

function tierBadgeVariant(tier: string) {
  if (tier === 'paid') return 'success' as const
  if (tier === 'trial') return 'warning' as const
  return 'default' as const
}

// --- Tabs ---

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'activity', label: 'Activity' },
  { id: 'results', label: 'Results' },
  { id: 'transactions', label: 'Transactions' },
]

// --- Main Component ---

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const { presenting } = usePresentation()
  const [activeTab, setActiveTab] = useState('overview')

  const { data: user, isLoading } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => api.get<UserProfile>(`/api/admin/users/${userId}`),
    enabled: !!userId,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center py-24 text-[var(--content-tertiary)]">
        User not found
      </div>
    )
  }

  return (
    <div>
      <Link
        to="/users"
        className="inline-flex items-center gap-1 text-sm text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] mb-4 transition-colors"
      >
        &larr; Back to Users
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-3">
            <PiiField value={user.name ?? user.email} presenting={presenting} />
            <Badge variant={tierBadgeVariant(user.tier)}>{user.tier}</Badge>
          </h1>
          <p className="mt-1 text-sm text-[var(--content-secondary)]">
            <PiiField value={user.email} presenting={presenting} />
          </p>
        </div>
        <div className="text-right text-sm text-[var(--content-tertiary)]">
          <p>ID: {user.user_id.slice(0, 8)}...</p>
          <p>Joined: {formatDate(user.created_at)}</p>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Credits" value={user.total_credits.toLocaleString()} />
        <StatCard label="Current Streak" value={`${user.daily_streak} days`} />
        <StatCard label="Days Active" value={user.total_days_active.toString()} />
        <StatCard
          label="MRR"
          value={user.stripe_mrr ? `$${(user.stripe_mrr / 100).toFixed(2)}` : '--'}
        />
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-6" />

      {activeTab === 'overview' && <OverviewTab user={user} presenting={presenting} />}
      {activeTab === 'activity' && <ActivityTab userId={user.user_id} />}
      {activeTab === 'results' && <ResultsTab userId={user.user_id} />}
      {activeTab === 'transactions' && <TransactionsTab userId={user.user_id} />}
    </div>
  )
}

// --- Stat Card ---

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-xs text-[var(--content-tertiary)] uppercase tracking-wider mb-1">{label}</p>
        <p className="text-xl font-heading font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-[var(--content-tertiary)]">{label}</span>
      <span>{value}</span>
    </div>
  )
}

// --- Overview Tab ---

function OverviewTab({ user, presenting }: { user: UserProfile; presenting: boolean }) {
  const queryClient = useQueryClient()
  const [grantDays, setGrantDays] = useState(14)
  const [granting, setGranting] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const hasTempAccess = !!user.temp_access_granted_by
  const canGrant = user.tier === 'free' && !hasTempAccess

  async function handleGrant() {
    setGranting(true)
    setActionError(null)
    try {
      await authApi.post('/api/v1/admin/grant-temp-access', {
        userId: user.user_id,
        durationDays: grantDays,
      })
      queryClient.invalidateQueries({ queryKey: ['admin-user', user.user_id] })
    } catch (e) {
      setActionError(String(e))
    } finally {
      setGranting(false)
    }
  }

  async function handleRevoke() {
    setRevoking(true)
    setActionError(null)
    try {
      await authApi.post('/api/v1/admin/revoke-temp-access', {
        userId: user.user_id,
      })
      queryClient.invalidateQueries({ queryKey: ['admin-user', user.user_id] })
    } catch (e) {
      setActionError(String(e))
    } finally {
      setRevoking(false)
    }
  }

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <h3 className="font-heading font-semibold text-sm">Membership</h3>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Row label="Status" value={user.stripe_status ?? '--'} />
          <Row label="MRR" value={user.stripe_mrr ? `$${(user.stripe_mrr / 100).toFixed(2)}` : '--'} />
          {user.stripe_current_period_end && <Row label="Period Ends" value={formatDate(user.stripe_current_period_end)} />}
          {user.trial_ends_at && <Row label="Trial Ends" value={formatDate(user.trial_ends_at)} />}
          {user.stripe_cancel_at && <Row label="Cancels At" value={formatDate(user.stripe_cancel_at)} />}

          {/* Temp access info */}
          {hasTempAccess && (
            <div className="pt-2 mt-2 border-t border-[var(--border)] space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="warning">Temp Access</Badge>
              </div>
              <Row label="Expires" value={formatDate(user.trial_ends_at)} />
              <Row label="Granted" value={formatDate(user.temp_access_granted_at)} />
              <Row label="Granted By" value={user.temp_access_granted_by?.slice(0, 8) + '...'} />
              <button
                onClick={handleRevoke}
                disabled={revoking}
                className="w-full mt-1 px-3 py-1.5 text-xs rounded-full bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 disabled:opacity-50 transition-colors cursor-pointer"
              >
                {revoking ? 'Revoking...' : 'Revoke Temp Access'}
              </button>
            </div>
          )}

          {/* Grant temp access */}
          {canGrant && (
            <div className="pt-2 mt-2 border-t border-[var(--border)] space-y-2">
              <p className="text-xs text-[var(--content-tertiary)]">Grant temporary content access</p>
              <div className="flex items-center gap-2">
                <select
                  value={grantDays}
                  onChange={(e) => setGrantDays(Number(e.target.value))}
                  className="flex-1 px-2 py-1.5 text-xs rounded-lg bg-[var(--surface-nested)] border border-[var(--border)] text-[var(--content)] cursor-pointer"
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                </select>
                <button
                  onClick={handleGrant}
                  disabled={granting}
                  className="px-3 py-1.5 text-xs rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {granting ? 'Granting...' : 'Grant Access'}
                </button>
              </div>
            </div>
          )}

          {actionError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{actionError}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="font-heading font-semibold text-sm">Account</h3>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-[var(--content-tertiary)]">Email</span>
            <PiiField value={user.email} presenting={presenting} />
          </div>
          <Row label="Joined" value={formatDate(user.created_at)} />
          <Row label="Last Active" value={user.last_activity_date ?? 'Never'} />
          <Row label="Longest Streak" value={`${user.longest_streak} days`} />
        </CardContent>
      </Card>

      {user.exerciseStats.length > 0 && (
        <Card className="md:col-span-2">
          <CardHeader>
            <h3 className="font-heading font-semibold text-sm">Exercise Summary</h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {user.exerciseStats.map((stat) => (
                <div key={stat.tool} className="text-center">
                  <p className="text-xs text-[var(--content-tertiary)] mb-1">
                    {toolLabels[stat.tool] ?? stat.tool}
                  </p>
                  <p className="text-lg font-heading font-semibold tabular-nums">{stat.attempts}</p>
                  <p className="text-xs text-[var(--content-secondary)]">
                    {stat.avg_score > 0 ? `${stat.avg_score.toFixed(0)}% avg` : 'no scores'}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <FreeLicensesCard userId={user.user_id} />
      <FreeTeacherSeatsCard userId={user.user_id} />
    </div>
  )
}

// --- Free Student Licenses Card ---

interface LicenseGrant {
  id: string
  license_count: number
  granted_by: string
  granted_at: string
  notes: string | null
}

interface LicenseGrantsResponse {
  available: number
  assigned: number
  grants: LicenseGrant[]
}

function FreeLicensesCard({ userId }: { userId: string }) {
  const queryClient = useQueryClient()
  const [licenseCount, setLicenseCount] = useState(5)
  const [notes, setNotes] = useState('')
  const [granting, setGranting] = useState(false)
  const [grantError, setGrantError] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-free-licenses', userId],
    queryFn: () => teacherApi.get<LicenseGrantsResponse>(`/api/v1/admin/free-license-grants/${userId}`),
  })

  // If the teacher API returns 404 (no teacher profile), don't show this card
  if (isError || isLoading) return null

  async function handleGrant() {
    setGranting(true)
    setGrantError(null)
    try {
      await teacherApi.post('/api/v1/admin/grant-free-licenses', {
        teacherUserId: userId,
        licenseCount,
        notes: notes.trim() || undefined,
      })
      setNotes('')
      queryClient.invalidateQueries({ queryKey: ['admin-free-licenses', userId] })
    } catch (e) {
      setGrantError(String(e))
    } finally {
      setGranting(false)
    }
  }

  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <h3 className="font-heading font-semibold text-sm">Free Student Licenses</h3>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {/* Current status */}
        <div className="flex gap-6">
          <div>
            <span className="text-[var(--content-tertiary)]">Available</span>
            <p className="text-2xl font-heading font-semibold tabular-nums">{data?.available ?? 0}</p>
          </div>
          <div>
            <span className="text-[var(--content-tertiary)]">Assigned</span>
            <p className="text-2xl font-heading font-semibold tabular-nums">{data?.assigned ?? 0}</p>
          </div>
        </div>

        {/* Grant form */}
        <div className="pt-2 border-t border-[var(--border)] space-y-2">
          <p className="text-xs text-[var(--content-tertiary)]">Grant free student licenses to this teacher</p>
          <div className="flex items-center gap-2">
            <select
              value={licenseCount}
              onChange={(e) => setLicenseCount(Number(e.target.value))}
              className="px-2 py-1.5 text-xs rounded-lg bg-[var(--surface-nested)] border border-[var(--border)] text-[var(--content)] cursor-pointer"
            >
              <option value={1}>1 license</option>
              <option value={3}>3 licenses</option>
              <option value={5}>5 licenses</option>
              <option value={10}>10 licenses</option>
              <option value={20}>20 licenses</option>
              <option value={50}>50 licenses</option>
            </select>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optional)"
              className="flex-1 px-2 py-1.5 text-xs rounded-lg bg-[var(--surface-nested)] border border-[var(--border)] text-[var(--content)] placeholder:text-[var(--content-tertiary)]"
            />
            <button
              onClick={handleGrant}
              disabled={granting}
              className="px-3 py-1.5 text-xs rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] disabled:opacity-50 transition-colors cursor-pointer"
            >
              {granting ? 'Granting...' : 'Grant'}
            </button>
          </div>
          {grantError && (
            <p className="text-xs text-red-600 dark:text-red-400">{grantError}</p>
          )}
        </div>

        {/* Grant history */}
        {data?.grants && data.grants.length > 0 && (
          <div className="pt-2 border-t border-[var(--border)]">
            <p className="text-xs font-medium text-[var(--content-tertiary)] uppercase tracking-wider mb-2">Grant History</p>
            <div className="space-y-1.5">
              {data.grants.map((grant) => (
                <div key={grant.id} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <Badge variant="default">+{grant.license_count}</Badge>
                    {grant.notes && (
                      <span className="text-[var(--content-secondary)]">{grant.notes}</span>
                    )}
                  </div>
                  <span className="text-[var(--content-tertiary)] tabular-nums">
                    {new Date(grant.granted_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// --- Free Teacher Seats Card (for studio owners) ---

interface StudioSeatUsage {
  used: number
  limit: number
  free: number
  available: number
}

interface StudioWithSeats {
  id: string
  name: string
  owner_user_id: string
  teacher_seat_limit: number
  free_teacher_seats: number
  seat_usage: StudioSeatUsage
}

interface SeatGrant {
  id: string
  studio_id: string
  seat_count: number
  granted_by: string
  granted_by_email: string | null
  granted_at: string
  notes: string | null
}

function FreeTeacherSeatsCard({ userId }: { userId: string }) {
  const queryClient = useQueryClient()
  const [selectedStudio, setSelectedStudio] = useState<string | null>(null)
  const [seatCount, setSeatCount] = useState(1)
  const [notes, setNotes] = useState('')
  const [granting, setGranting] = useState(false)
  const [grantError, setGrantError] = useState<string | null>(null)

  // Fetch studios owned by this user
  const { data: studiosData, isLoading: studiosLoading } = useQuery({
    queryKey: ['admin-studios-by-owner', userId],
    queryFn: () =>
      studioApi.get<{ studios: StudioWithSeats[] }>(`/api/v1/admin/studios`, {
        params: { owner_user_id: userId },
      }),
  })

  const studios = studiosData?.studios ?? []

  // If no studios or still loading, don't render
  if (studiosLoading || studios.length === 0) return null

  // Auto-select first studio if none selected
  const activeStudioId = selectedStudio ?? studios[0]?.id
  const activeStudio = studios.find((s) => s.id === activeStudioId)

  // Fetch grant history for active studio
  const { data: grantsData } = useQuery({
    queryKey: ['admin-seat-grants', activeStudioId],
    queryFn: () =>
      studioApi.get<{ usage: StudioSeatUsage; grants: SeatGrant[] }>(
        `/api/v1/admin/teacher-seat-grants/${activeStudioId}`,
      ),
    enabled: !!activeStudioId,
  })

  async function handleGrant() {
    if (!activeStudioId) return
    setGranting(true)
    setGrantError(null)
    try {
      await studioApi.post('/api/v1/admin/grant-teacher-seats', {
        studio_id: activeStudioId,
        seat_count: seatCount,
        notes: notes.trim() || undefined,
      })
      setNotes('')
      queryClient.invalidateQueries({ queryKey: ['admin-seat-grants', activeStudioId] })
      queryClient.invalidateQueries({ queryKey: ['admin-studios-by-owner', userId] })
    } catch (e) {
      setGrantError(String(e))
    } finally {
      setGranting(false)
    }
  }

  const usage = grantsData?.usage ?? activeStudio?.seat_usage

  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <h3 className="font-heading font-semibold text-sm">Free Teacher Seats (Studio)</h3>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {/* Studio selector (if multiple) */}
        {studios.length > 1 && (
          <select
            value={activeStudioId}
            onChange={(e) => setSelectedStudio(e.target.value)}
            className="px-2 py-1.5 text-xs rounded-lg bg-[var(--surface-nested)] border border-[var(--border)] text-[var(--content)] cursor-pointer"
          >
            {studios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}

        {/* Current status */}
        <div className="flex gap-6">
          <div>
            <span className="text-[var(--content-tertiary)]">Seats Used</span>
            <p className="text-2xl font-heading font-semibold tabular-nums">
              {usage?.used ?? 0}/{usage?.limit ?? 1}
            </p>
          </div>
          <div>
            <span className="text-[var(--content-tertiary)]">Free Seats</span>
            <p className="text-2xl font-heading font-semibold tabular-nums">
              {usage?.free ?? 0}
            </p>
          </div>
          <div>
            <span className="text-[var(--content-tertiary)]">Available</span>
            <p className="text-2xl font-heading font-semibold tabular-nums">
              {usage?.available ?? 0}
            </p>
          </div>
        </div>

        {/* Grant form */}
        <div className="pt-2 border-t border-[var(--border)] space-y-2">
          <p className="text-xs text-[var(--content-tertiary)]">
            Grant free teacher seats to this studio
          </p>
          <div className="flex items-center gap-2">
            <select
              value={seatCount}
              onChange={(e) => setSeatCount(Number(e.target.value))}
              className="px-2 py-1.5 text-xs rounded-lg bg-[var(--surface-nested)] border border-[var(--border)] text-[var(--content)] cursor-pointer"
            >
              <option value={1}>1 seat</option>
              <option value={2}>2 seats</option>
              <option value={3}>3 seats</option>
              <option value={5}>5 seats</option>
              <option value={10}>10 seats</option>
            </select>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optional)"
              className="flex-1 px-2 py-1.5 text-xs rounded-lg bg-[var(--surface-nested)] border border-[var(--border)] text-[var(--content)] placeholder:text-[var(--content-tertiary)]"
            />
            <button
              onClick={handleGrant}
              disabled={granting}
              className="px-3 py-1.5 text-xs rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] disabled:opacity-50 transition-colors cursor-pointer"
            >
              {granting ? 'Granting...' : 'Grant'}
            </button>
          </div>
          {grantError && (
            <p className="text-xs text-red-600 dark:text-red-400">{grantError}</p>
          )}
        </div>

        {/* Grant history */}
        {grantsData?.grants && grantsData.grants.length > 0 && (
          <div className="pt-2 border-t border-[var(--border)]">
            <p className="text-xs font-medium text-[var(--content-tertiary)] uppercase tracking-wider mb-2">
              Grant History
            </p>
            <div className="space-y-1.5">
              {grantsData.grants.map((grant) => (
                <div key={grant.id} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <Badge variant="default">+{grant.seat_count}</Badge>
                    {grant.notes && (
                      <span className="text-[var(--content-secondary)]">{grant.notes}</span>
                    )}
                  </div>
                  <span className="text-[var(--content-tertiary)] tabular-nums">
                    {new Date(grant.granted_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// --- Activity Tab ---

function ActivityTab({ userId }: { userId: string }) {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)

  const { data: heatmap, isLoading } = useQuery({
    queryKey: ['admin-heatmap', userId, year],
    queryFn: () =>
      api.get<HeatmapResponse>(`/api/v1/heatmap/${userId}`, {
        params: { year },
      }),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    )
  }

  const stats = heatmap?.stats
  const days = heatmap?.days ?? []

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Active Days" value={stats?.total_days?.toString() ?? '0'} />
        <StatCard label="Current Streak" value={`${stats?.current_streak ?? 0} days`} />
        <StatCard label="Longest Streak" value={`${stats?.longest_streak ?? 0} days`} />
        <StatCard label="This Month" value={`${stats?.this_month_days ?? 0} / ${stats?.this_month_total ?? 0} days`} />
      </div>

      {/* Heatmap */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-sm">Activity Heatmap</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setYear(y => y - 1)}
                disabled={year <= 2020}
                className="px-2 py-1 text-xs rounded bg-[var(--surface-nested)] text-[var(--content-secondary)] hover:bg-[var(--border)] disabled:opacity-40 transition-colors cursor-pointer"
              >
                &larr;
              </button>
              <span className="text-sm tabular-nums font-medium">{year}</span>
              <button
                onClick={() => setYear(y => y + 1)}
                disabled={year >= currentYear}
                className="px-2 py-1 text-xs rounded bg-[var(--surface-nested)] text-[var(--content-secondary)] hover:bg-[var(--border)] disabled:opacity-40 transition-colors cursor-pointer"
              >
                &rarr;
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Heatmap year={year} days={days} />
        </CardContent>
      </Card>

      {/* Recent activity table */}
      {days.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-heading font-semibold text-sm">Recent Activity</h3>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--content-secondary)] uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[var(--content-secondary)] uppercase tracking-wider">Total</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[var(--content-secondary)] uppercase tracking-wider">Ear</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[var(--content-secondary)] uppercase tracking-wider">Sight</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[var(--content-secondary)] uppercase tracking-wider">Knowledge</th>
                </tr>
              </thead>
              <tbody>
                {[...days]
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .slice(0, 20)
                  .map((d) => (
                    <tr key={d.date} className="border-b border-[var(--border-subtle)]">
                      <td className="px-4 py-2.5 tabular-nums">{d.date}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium">{d.total_min}m</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-[var(--content-secondary)]">{d.ear_min}m</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-[var(--content-secondary)]">{d.sight_min}m</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-[var(--content-secondary)]">{d.knowledge_min}m</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// --- Heatmap Component ---

function Heatmap({ year, days }: { year: number; days: HeatmapDay[] }) {
  const { cells, months } = useMemo(() => {
    const dayMap = new Map(days.map((d) => [d.date, d.total_min]))

    // Build grid: 53 weeks × 7 days
    const jan1 = new Date(year, 0, 1)
    const startDow = jan1.getDay() // 0=Sun
    const startDate = new Date(jan1)
    startDate.setDate(startDate.getDate() - startDow)

    const cellsArr: { date: string; minutes: number; level: number; inYear: boolean }[] = []
    const monthLabels: { label: string; col: number }[] = []
    let lastMonth = -1
    const d = new Date(startDate)

    for (let week = 0; week < 53; week++) {
      for (let dow = 0; dow < 7; dow++) {
        const iso = d.toISOString().slice(0, 10)
        const mins = dayMap.get(iso) ?? 0
        const inYear = d.getFullYear() === year
        const level = !inYear || mins === 0 ? 0 : mins < 10 ? 1 : mins < 30 ? 2 : mins < 60 ? 3 : 4

        if (dow === 0 && d.getMonth() !== lastMonth && d.getFullYear() === year) {
          monthLabels.push({ label: d.toLocaleString('en', { month: 'short' }), col: week })
          lastMonth = d.getMonth()
        }

        cellsArr.push({ date: iso, minutes: mins, level, inYear })
        d.setDate(d.getDate() + 1)
      }
    }

    return { cells: cellsArr, months: monthLabels }
  }, [year, days])

  const levelColors = [
    'bg-[var(--surface-nested)]',
    'bg-emerald-200 dark:bg-emerald-900/50',
    'bg-emerald-400 dark:bg-emerald-700/70',
    'bg-emerald-600 dark:bg-emerald-500/80',
    'bg-emerald-800 dark:bg-emerald-400',
  ]

  return (
    <div className="overflow-x-auto">
      {/* Month labels */}
      <div className="grid gap-[3px] mb-1" style={{ gridTemplateColumns: 'repeat(53, 12px)', paddingLeft: 0 }}>
        {Array.from({ length: 53 }, (_, i) => {
          const m = months.find((ml) => ml.col === i)
          return (
            <div key={i} className="text-[10px] text-[var(--content-tertiary)] leading-none h-3">
              {m?.label ?? ''}
            </div>
          )
        })}
      </div>
      {/* Grid: rows = days of week (Mon-Sun), cols = weeks */}
      <div className="grid gap-[3px]" style={{ gridTemplateRows: 'repeat(7, 12px)', gridTemplateColumns: 'repeat(53, 12px)', gridAutoFlow: 'column' }}>
        {cells.map((c, i) => (
          <div
            key={i}
            className={`w-3 h-3 rounded-[2px] ${c.inYear ? levelColors[c.level] : 'bg-transparent'}`}
            title={c.inYear ? `${c.date}: ${c.minutes}m` : ''}
          />
        ))}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-1 mt-2 text-[10px] text-[var(--content-tertiary)]">
        <span>Less</span>
        {levelColors.map((cls, i) => (
          <div key={i} className={`w-3 h-3 rounded-[2px] ${cls}`} />
        ))}
        <span>More</span>
      </div>
    </div>
  )
}

// --- Results Tab ---

function ResultsTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1)
  const [tool, setTool] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-user-results', userId, page, tool],
    queryFn: () =>
      api.get<ResultsResponse>(`/api/admin/users/${userId}/results`, {
        params: { page, limit: 20, tool: tool || undefined },
      }),
  })

  const { data: detail } = useQuery({
    queryKey: ['admin-result-detail', userId, expandedId],
    queryFn: () =>
      api.get<ResultDetail>(`/api/admin/users/${userId}/results/${expandedId}`),
    enabled: !!expandedId,
  })

  const columns: Column<ExerciseResult>[] = [
    {
      id: 'date',
      header: 'Date',
      cell: (r) => <span className="tabular-nums">{r.date}</span>,
    },
    {
      id: 'tool',
      header: 'Tool',
      cell: (r) => <span>{toolLabels[r.tool] ?? r.tool}</span>,
    },
    {
      id: 'exercise_id',
      header: 'Exercise',
      cell: (r) => <span className="text-[var(--content-secondary)] text-xs">{r.exercise_id}</span>,
    },
    {
      id: 'comparison_score',
      header: 'Score',
      cell: (r) =>
        r.comparison_score != null ? (
          <span className="tabular-nums font-medium">{r.comparison_score.toFixed(0)}%</span>
        ) : (
          <span className="text-[var(--content-tertiary)]">--</span>
        ),
    },
    {
      id: 'passed',
      header: 'Passed',
      cell: (r) => (
        <Badge variant={r.passed ? 'success' : 'error'}>{r.passed ? 'Yes' : 'No'}</Badge>
      ),
    },
    {
      id: 'duration_ms',
      header: 'Duration',
      cell: (r) => <span className="tabular-nums text-[var(--content-secondary)]">{formatDuration(r.duration_ms)}</span>,
    },
  ]

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-3">
        <select
          value={tool}
          onChange={(e) => { setTool(e.target.value); setPage(1) }}
          className="px-3 py-1.5 text-sm rounded-lg bg-[var(--surface-elevated)] border border-[var(--border)] text-[var(--content)] cursor-pointer"
        >
          <option value="">All Tools</option>
          {Object.entries(toolLabels).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        {data && (
          <span className="text-sm text-[var(--content-tertiary)]">
            {data.total} result{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            data={data?.results ?? []}
            keyExtractor={(r) => r.id}
            onRowClick={(r) => setExpandedId(expandedId === r.id ? null : r.id)}
            page={page}
            totalPages={data?.totalPages ?? 1}
            onPageChange={setPage}
            loading={isLoading}
            emptyMessage="No exercise results"
          />
        </CardContent>
      </Card>

      {/* Expanded result detail */}
      {expandedId && detail && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-sm">
                Result Detail — {toolLabels[detail.tool] ?? detail.tool}
              </h3>
              <button
                onClick={() => setExpandedId(null)}
                className="text-xs text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] cursor-pointer"
              >
                Close
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4 mb-4 text-sm">
              <Row label="Exercise" value={detail.exercise_id} />
              <Row label="Score" value={detail.comparison_score != null ? `${detail.comparison_score.toFixed(1)}%` : '--'} />
              <Row label="Duration" value={formatDuration(detail.duration_ms)} />
            </div>
            {detail.result_data && (
              <div>
                <p className="text-xs font-medium text-[var(--content-tertiary)] uppercase tracking-wider mb-2">Result Data</p>
                <pre className="text-xs bg-[var(--surface-nested)] p-4 rounded-lg overflow-x-auto max-h-96 whitespace-pre-wrap">
                  {JSON.stringify(detail.result_data, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// --- Transactions Tab ---

function TransactionsTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-user-transactions', userId, page],
    queryFn: () =>
      api.get<TransactionsResponse>(`/api/admin/users/${userId}/transactions`, {
        params: { page, limit: 50 },
      }),
  })

  const columns: Column<Transaction>[] = [
    {
      id: 'created_at',
      header: 'Date',
      cell: (r) => <span className="tabular-nums">{formatDate(r.created_at)}</span>,
    },
    {
      id: 'amount',
      header: 'Amount',
      cell: (r) => (
        <span className={`tabular-nums font-medium ${r.amount > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
          {r.amount > 0 ? '+' : ''}{r.amount}
        </span>
      ),
    },
    {
      id: 'reason',
      header: 'Reason',
      cell: (r) => <span className="text-[var(--content-secondary)]">{r.reason}</span>,
    },
    {
      id: 'balance_after',
      header: 'Balance',
      cell: (r) => <span className="tabular-nums">{r.balance_after?.toLocaleString() ?? '--'}</span>,
    },
  ]

  return (
    <Card>
      <CardContent className="p-0">
        <DataTable
          columns={columns}
          data={data?.transactions ?? []}
          keyExtractor={(r) => r.id.toString()}
          page={page}
          totalPages={data?.totalPages ?? 1}
          onPageChange={setPage}
          loading={isLoading}
          emptyMessage="No transactions"
        />
      </CardContent>
    </Card>
  )
}
